import { BaseFormatAdapter } from './base-format.adapter';
import {
    Metamodel,
    DataFlowDSLAdapterRegistry,
    stringIsNullOrEmpty,
    AzureBlobFSLinkedService,
    Owned
} from 'shell-models-lib';
import { CdmSettings, CdmManifestType, EntityReferenceType } from 'app/model/dataflow/dataset-settings/cdm-settings';
import { DataFlowExpressionUtils } from '../expression.util';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { GitHubLinkedService } from 'app/model/linkedservice/github.linkedservice';
import { BooleanDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DelimitedTextSettings } from 'app/model/dataflow/dataset-settings/delimited-text-settings';
import { string2UnicodeSingle, unicode2StrSingle } from 'app/shared/utils/string.util';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { LinkedServiceUtils } from 'app/model/linkedservice/linked-service.utils';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(CdmSettings)
})
export class CdmFormatAdapter extends BaseFormatAdapter<CdmSettings> {
    protected async toSourceDSL(settings: CdmSettings): Promise<string[]> {
        const props = [];
        const model = settings.owner as DataFlowSource;
        props.push(`entity: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.entity)}`);
        props.push(`format: '${DatasetFormatType.CDM}'`);
        props.push(`manifestType: '${settings.manifestType}'`);
        const manifestName = settings.manifestName;
        if (settings.manifestType !== CdmManifestType.MODEL) {
            if (!stringIsNullOrEmpty(manifestName.expression)) {
                props.push(`manifestName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(manifestName)}`);
            }
            if (!stringIsNullOrEmpty(settings.entityPath.expression)) {
                props.push(`entityPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.entityPath)}`);
            }
            if (settings.isLocalEntityReference) {
                props.push(`local: true`);
            } else if (settings.isCustomEntityReference && model.schemaLinkedService) {
                props.push(`corpusPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.corpusPath)}`);
                if (model.schemaLinkedService instanceof AzureBlobFSLinkedService) {
                    props.push(`corpusStore: 'adlsgen2'`);
                    props.push(
                        `adlsgen2_fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`
                    );
                } else if (model.schemaLinkedService instanceof GitHubLinkedService) {
                    props.push(`corpusStore: 'github'`);
                    props.push(
                        `github_repository: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.repository)}`
                    );
                    props.push(`github_branch: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.branch)}`);
                }
            }
            // else use a standard entity
        }
        props.push(
            `folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.subFormatSettings.folderPath)}`
        );
        if (LinkedServiceUtils.isBucketNameTypeInBackend(model.sourceLinkedService)) {
            props.push(
                `bucketName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.subFormatSettings.container)}`
            );
        } else {
            props.push(
                `fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.subFormatSettings.container)}`
            );
        }
        return props;
    }

    protected async toSinkDSL(setting: CdmSettings): Promise<string[]> {
        const props = [];
        const model = setting.owner as DataFlowSink;
        props.push(`entity: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.entity)}`);
        props.push(`format: '${DatasetFormatType.CDM}'`);
        props.push(`manifestType: '${setting.manifestType}'`);
        if (!stringIsNullOrEmpty(setting.entityPath.expression)) {
            props.push(`entityPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.entityPath)}`);
        }
        if (!stringIsNullOrEmpty(setting.manifestName.expression)) {
            props.push(`manifestName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.manifestName)}`);
        }
        if (!stringIsNullOrEmpty(setting.partitionPath.expression)) {
            props.push(`partitionPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.partitionPath)}`);
        }
        props.push(
            `folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.subFormatSettings.folderPath)}`
        );
        if (LinkedServiceUtils.isBucketNameTypeInBackend(model.sinkLinkedService)) {
            props.push(
                `bucketName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.subFormatSettings.container)}`
            );
        } else {
            props.push(
                `fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.subFormatSettings.container)}`
            );
        }

        if (setting.getSubFormat() === DatasetFormatType.Delimited) {
            const subFormat = setting.subFormatSettings as DelimitedTextSettings;
            props.push(
                `columnNamesAsHeader: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    subFormat.columnNamesAsHeader,
                    BooleanDataFlowType.TYPE
                )}`
            );
            if (!stringIsNullOrEmpty(subFormat.columnDelimiter.expression)) {
                if (subFormat.columnDelimiter.templateExpression) {
                    props.push(
                        `columnDelimiter: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            subFormat.columnDelimiter
                        )}`
                    );
                } else {
                    const transformedColDelimiterExpression = new DataFlowExpression();
                    transformedColDelimiterExpression.expression = JSON.stringify(
                        string2UnicodeSingle(subFormat.columnDelimiter.expression)
                    );
                    transformedColDelimiterExpression.expression =
                        transformedColDelimiterExpression.expression.substring(
                            1,
                            transformedColDelimiterExpression.expression.length - 1
                        );
                    props.push(
                        `columnDelimiter: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            transformedColDelimiterExpression
                        )}`
                    );
                }
            }
            if (!stringIsNullOrEmpty(subFormat.encoding.expression)) {
                props.push(`encodingName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(subFormat.encoding)}`);
            }
            if (!stringIsNullOrEmpty(subFormat.quoteCharacter.expression)) {
                if (subFormat.quoteCharacter.templateExpression) {
                    props.push(
                        `quoteChar: ${DataFlowExpressionUtils.wrapExpressionConfigValue(subFormat.quoteCharacter)}`
                    );
                } else {
                    const transformedQuoteCharExpression = new DataFlowExpression();
                    transformedQuoteCharExpression.expression = JSON.stringify(
                        string2UnicodeSingle(subFormat.quoteCharacter.expression)
                    );
                    transformedQuoteCharExpression.expression = transformedQuoteCharExpression.expression.substring(
                        1,
                        transformedQuoteCharExpression.expression.length - 1
                    );
                    props.push(
                        `quoteChar: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            transformedQuoteCharExpression
                        )}`
                    );
                }
            }
            if (!stringIsNullOrEmpty(subFormat.escapeCharacter.expression)) {
                if (subFormat.escapeCharacter.templateExpression) {
                    props.push(
                        `escapeChar: ${DataFlowExpressionUtils.wrapExpressionConfigValue(subFormat.escapeCharacter)}`
                    );
                } else {
                    const transformedEscapeCharExpression = new DataFlowExpression();
                    transformedEscapeCharExpression.expression = JSON.stringify(
                        string2UnicodeSingle(subFormat.escapeCharacter.expression)
                    );
                    transformedEscapeCharExpression.expression = transformedEscapeCharExpression.expression.substring(
                        1,
                        transformedEscapeCharExpression.expression.length - 1
                    );
                    props.push(
                        `escapeChar: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            transformedEscapeCharExpression
                        )}`
                    );
                }
            }
        } else if (setting.getSubFormat() === DatasetFormatType.Parquet) {
            props.push(`subformat: '${DatasetFormatType.Parquet}'`);
        } else if (setting.getSubFormat() === DatasetFormatType.Delta) {
            props.push(`subformat: '${DatasetFormatType.Delta}'`);
        }
        if (setting.isCustomEntityReference && (setting.owner as DataFlowSink).schemaLinkedService) {
            props.push(`corpusPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.corpusPath)}`);
            if ((setting.owner as DataFlowSink).schemaLinkedService instanceof AzureBlobFSLinkedService) {
                props.push(`corpusStore: 'adlsgen2'`);
                props.push(
                    `adlsgen2_fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.container)}`
                );
            } else if ((setting.owner as DataFlowSink).schemaLinkedService instanceof GitHubLinkedService) {
                props.push(`corpusStore: 'github'`);
                props.push(
                    `github_repository: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.repository)}`
                );
                props.push(`github_branch: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.branch)}`);
            }
        }
        if (!stringIsNullOrEmpty(setting.truncate.expression)) {
            props.push(
                `truncate: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.truncate,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }

        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<CdmSettings> {
        const settings = Owned.create<CdmSettings>(CdmSettings.MODEL, owner);

        const subFormat = configs.get('subformat')
            ? DataFlowExpressionUtils.extractStringConfigValue(configs.get('subformat'))
            : DatasetFormatType.Delimited;
        settings.initialize(subFormat);
        handledConfigs.push('subformat');

        if (configs.has('entity')) {
            settings.entity = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('entity'), owner);
            handledConfigs.push('entity');
        }
        if (configs.has('manifestName')) {
            settings.manifestName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('manifestName'),
                owner
            );
            handledConfigs.push('manifestName');
        }
        if (configs.has('manifestType')) {
            settings.manifestType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('manifestType')
            ) as CdmManifestType;
        }
        if (configs.has('entityPath')) {
            settings.entityPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('entityPath'), owner);
            handledConfigs.push('entityPath');
        }
        if (configs.has('corpusPath')) {
            settings.corpusPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('corpusPath'), owner);
            handledConfigs.push('corpusPath');
        }
        if (configs.has('folderPath')) {
            settings.subFormatSettings.folderPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('folderPath'),
                owner
            );
            handledConfigs.push('folderPath');
        }
        if (configs.has('fileSystem')) {
            settings.subFormatSettings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('fileSystem'),
                owner
            );
            handledConfigs.push('fileSystem');
        } else if (configs.has('bucketName')) {
            settings.subFormatSettings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('bucketName'),
                owner
            );
            handledConfigs.push('bucketName');
        }
        if (settings.manifestType === CdmManifestType.MANIFEST) {
            if (configs.has('local') && configs.get('local') === 'true') {
                settings.entityReferenceType = EntityReferenceType.LOCAL;
                handledConfigs.push('local');
            } else if (configs.has('corpusStore')) {
                const storeType = DataFlowExpressionUtils.extractStringConfigValue(configs.get('corpusStore'));
                handledConfigs.push('corpusStore');
                if (storeType === 'adlsgen2') {
                    if (configs.has('adlsgen2_fileSystem')) {
                        settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            configs.get('adlsgen2_fileSystem'),
                            owner
                        );
                        handledConfigs.push('adlsgen2_fileSystem');
                    }
                } else if (storeType === 'github') {
                    if (configs.has('github_repository')) {
                        settings.repository = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            configs.get('github_repository'),
                            owner
                        );
                        handledConfigs.push('github_repository');
                    }
                    if (configs.has('github_branch')) {
                        settings.branch = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            configs.get('github_branch'),
                            owner
                        );
                        handledConfigs.push('github_branch');
                    }
                }
                settings.entityReferenceType = EntityReferenceType.CUSTOM;
            } else {
                settings.entityReferenceType = EntityReferenceType.STANDARD;
            }
        }

        return settings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<CdmSettings> {
        const settings = Owned.create<CdmSettings>(CdmSettings.MODEL, owner);

        const subFormat = configs.get('subformat')
            ? DataFlowExpressionUtils.extractStringConfigValue(configs.get('subformat'))
            : DatasetFormatType.Delimited;
        settings.initialize(subFormat);
        handledConfigs.push('subformat');

        if (configs.has('entity')) {
            settings.entity = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('entity'), owner);
            handledConfigs.push('entity');
        }
        if (configs.has('entityPath')) {
            settings.entityPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('entityPath'), owner);
            handledConfigs.push('entityPath');
        }
        if (configs.has('manifestName')) {
            settings.manifestName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('manifestName'),
                owner
            );
            handledConfigs.push('manifestName');
        }
        if (configs.has('corpusPath')) {
            settings.corpusPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('corpusPath'), owner);
            handledConfigs.push('corpusPath');
        }
        if (configs.has('folderPath')) {
            settings.subFormatSettings.folderPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('folderPath'),
                owner
            );
            handledConfigs.push('folderPath');
        }
        if (configs.has('truncate')) {
            settings.truncate = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('truncate'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('truncate');
        }
        if (configs.has('fileSystem')) {
            settings.subFormatSettings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('fileSystem'),
                owner
            );
            handledConfigs.push('fileSystem');
        } else if (configs.has('bucketName')) {
            settings.subFormatSettings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('bucketName'),
                owner
            );
            handledConfigs.push('bucketName');
        }
        if (configs.has('partitionPath')) {
            settings.partitionPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('partitionPath'),
                owner
            );
            handledConfigs.push('partitionPath');
        }
        if (subFormat === DatasetFormatType.Delimited) {
            const subFormat = settings.subFormatSettings as DelimitedTextSettings;
            if (configs.has('columnDelimiter')) {
                subFormat.columnDelimiter = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('columnDelimiter'),
                    owner
                );
                if (!subFormat.columnDelimiter.templateExpression) {
                    subFormat.columnDelimiter.expression = unicode2StrSingle(
                        JSON.parse(`"${subFormat.columnDelimiter.expression}"`)
                    );
                }
                handledConfigs.push('columnDelimiter');
            }
            if (configs.has('columnNamesAsHeader')) {
                subFormat.columnNamesAsHeader = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('columnNamesAsHeader'),
                    owner,
                    BooleanDataFlowType.TYPE
                );
                handledConfigs.push('columnNamesAsHeader');
            }
            if (configs.has('escapeChar')) {
                subFormat.escapeCharacter = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('escapeChar'),
                    owner
                );
                if (!subFormat.escapeCharacter.templateExpression) {
                    subFormat.escapeCharacter.expression = unicode2StrSingle(
                        JSON.parse(`"${subFormat.escapeCharacter.expression}"`)
                    );
                }
                handledConfigs.push('escapeChar');
            }
            if (configs.has('quoteChar')) {
                subFormat.quoteCharacter = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('quoteChar'),
                    owner
                );
                if (!subFormat.quoteCharacter.templateExpression) {
                    subFormat.quoteCharacter.expression = unicode2StrSingle(
                        JSON.parse(`"${subFormat.quoteCharacter.expression}"`)
                    );
                }
                handledConfigs.push('quoteChar');
            }
            if (configs.has('encodingName')) {
                subFormat.encoding = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('encodingName'),
                    owner
                );
                handledConfigs.push('encodingName');
            }
        }
        if (configs.has('corpusStore')) {
            const storeType = DataFlowExpressionUtils.extractStringConfigValue(configs.get('corpusStore'));
            handledConfigs.push('corpusStore');
            if (storeType === 'adlsgen2') {
                if (configs.has('adlsgen2_fileSystem')) {
                    settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        configs.get('adlsgen2_fileSystem'),
                        owner
                    );
                    handledConfigs.push('adlsgen2_fileSystem');
                }
            } else if (storeType === 'github') {
                if (configs.has('github_repository')) {
                    settings.repository = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        configs.get('github_repository'),
                        owner
                    );
                    handledConfigs.push('github_repository');
                }
                if (configs.has('github_branch')) {
                    settings.branch = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                        configs.get('github_branch'),
                        owner
                    );
                    handledConfigs.push('github_branch');
                }
            }
            settings.entityReferenceType = EntityReferenceType.CUSTOM;
        } else {
            settings.entityReferenceType = EntityReferenceType.STANDARD;
        }

        return settings;
    }
}
